import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  PlayCircle,
  Film,
  Feather,
  ImageIcon,
  Code,
  Layers,
  Sparkles,
  ChevronDown,
  ClipboardList,
} from "lucide-react";
import { Link } from "react-router-dom";

const SYLLABUS = [
  "History of Flash & multimedia (Flash → Animate CC → HTML5 Canvas)",
  "ActionScript 3.0 fundamentals & event model",
  "Timeline, keyframes, tweens & symbols",
  "Vector drawing, shape tweens & movie clips",
  "Interactivity: buttons, events, and simple UI",
  "Animation principles (easing, squash/stretch, timing)",
  "Exporting for web: HTML5 Canvas / Animate CC workflows",
  "Optimization & asset management (spritesheets, sounds)",
  "Intro to simple Flash games & interactive banners",
  "Porting Flash concepts to modern tools (Canvas, WebGL)",
];

// const LABS = [
//   {
//     title: "Animated Banner",
//     desc: "Create a looping ad banner with timeline keyframes and easing.",
//   },
//   {
//     title: "Interactive Button",
//     desc: "Build buttons and hover/click interactions using AS3-like logic.",
//   },
//   {
//     title: "Mini Platformer",
//     desc: "Simple 2D character movement & collisions (game basics).",
//   },
//   {
//     title: "Portfolio Reel",
//     desc: "Assemble exported clips into a web portfolio showcase.",
//   },
// ];

const LABS = [
  {
    title: "Animated Banner",
    desc: "Create a looping ad banner with timeline keyframes and easing.",
    tasks: [
      "Design banner layout & assets",
      "Animate using timeline keyframes",
      "Export for web display",
    ],
  },
  {
    title: "Interactive Button",
    desc: "Build buttons and hover/click interactions using AS3-like logic.",
    tasks: [
      "Add hover & click effects",
      "Handle events & animations",
      "Customize color transitions",
    ],
  },
  {
    title: "Mini Platformer",
    desc: "Simple 2D character movement & collisions (game basics).",
    tasks: [
      "Code player movement",
      "Add collision detection",
      "Implement jumping physics",
    ],
  },
  {
    title: "Portfolio Reel",
    desc: "Assemble exported clips into a web portfolio showcase.",
    tasks: [
      "Import animations & clips",
      "Arrange scenes in sequence",
      "Export as final showcase reel",
    ],
  },
];

const SKILLS = [
  "Frame-based Animation",
  "ActionScript 3.0 (concepts)",
  "Animate CC & HTML5 Canvas",
  "Sprite/Asset Optimization",
  "Interactive UI & Buttons",
  "Basic Game Logic",
  "Audio Sync & Timeline Editing",
  "Export & Web Integration",
];

const CAREERS = [
  "Motion Graphics Artist",
  "UI Animator / Microinteractions",
  "Multimedia Developer",
  "2D Game Artist / Prototype Developer",
  "Digital Ad Animator",
];

export default function Flash() {
  const [running, setRunning] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, []);

  const toggleRun = () => setRunning((r) => !r);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white text-slate-900 overflow-hidden">
      {/* HERO */}
      <header className="max-w-6xl mx-auto px-6 pt-5 pb-8 mt-5">
        <div
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-amber-50 to-rose-50 shadow-lg overflow-hidden"
          data-aos="zoom-in"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 items-center">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-rose-600/90 p-2 text-white shadow">
                  <PlayCircle className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                    Adobe Flash Studio — Animate, Script & Build
                  </h1>
                  <p className="mt-1 text-sm text-slate-600 max-w-2xl">
                    Learn frame-based animation, ActionScript concepts, timeline
                    workflows, and how to translate Flash-era skills to modern
                    web animation (Animate CC, Canvas).
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 text-white shadow hover:scale-[1.02] transition"
                >
                  <PlayCircle className="h-4 w-4" /> Enroll Now
                </Link>

                <button
                  onClick={() =>
                    window.scrollTo({ top: 600, behavior: "smooth" })
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-800 hover:bg-slate-50"
                >
                  <ClipboardList className="h-4 w-4" /> View Syllabus
                </button>

                <button
                  onClick={toggleRun}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${
                    running
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-slate-800 border-slate-200"
                  } hover:scale-[1.02] transition`}
                >
                  <Film className="h-4 w-4" />{" "}
                  {running ? "Stop Preview" : "Run Preview"}
                </button>
              </div>
            </div>

            {/* Preview Canvas */}
            <div className="relative">
              <div
                className="h-48 rounded-lg border border-slate-200 bg-gradient-to-br from-neutral-900/5 to-neutral-900/2 p-3 overflow-hidden"
                data-aos="fade-left"
              >
                <div className="flex items-center justify-center h-full">
                  <div className="w-full max-w-xs">
                    <div className="relative h-36 bg-white rounded-lg shadow-inner overflow-hidden">
                      <div className="absolute left-3 top-3 text-xs text-neutral-400">
                        Stage
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className={`w-20 h-20 rounded-md flex items-center justify-center text-white font-bold transition-transform duration-800 ${
                            running
                              ? "bg-rose-500 translate-y-[-6px] scale-110 animate-bounce"
                              : "bg-slate-400"
                          }`}
                        >
                          <span className="text-sm">Flash</span>
                        </div>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 text-neutral-500">
                          <Sparkles className="h-4 w-4 text-amber-400" />
                          <span>Timeline · 24fps</span>
                        </div>
                        <div className="text-neutral-500">Loop</div>
                      </div>
                    </div>
                    <div className="mt-2 text-center text-xs text-slate-500">
                      Preview Stage — click Run Preview
                    </div>
                  </div>
                </div>
                <div className="absolute -top-3 -right-3 hidden md:block">
                  <div className="rotate-12 rounded-full bg-amber-400 px-3 py-1 shadow text-sm font-semibold">
                    Retro Studio
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Syllabus */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="fade-up">
          Syllabus
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div data-aos="fade-right">
            <ul className="space-y-2">
              {SYLLABUS.slice(0, Math.ceil(SYLLABUS.length / 2)).map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="rounded px-2 py-1 bg-rose-50 text-rose-600 font-semibold text-sm flex items-center gap-1">
                    <Feather className="h-3 w-3" /> {i + 1}
                  </div>
                  <div className="font-medium">{s}</div>
                </li>
              ))}
            </ul>
          </div>
          <div data-aos="fade-left">
            <ul className="space-y-2">
              {SYLLABUS.slice(Math.ceil(SYLLABUS.length / 2)).map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="rounded px-2 py-1 bg-amber-50 text-amber-600 font-semibold text-sm flex items-center gap-1">
                    <Feather className="h-3 w-3" />{" "}
                    {i + 1 + Math.ceil(SYLLABUS.length / 2)}
                  </div>
                  <div className="font-medium">{s}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Labs */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="fade-up-right">
          Hands-on Labs & Projects
        </h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          {LABS.map((lab, i) => (
            <div
              key={i}
              className="group perspective"
              data-aos="zoom-in"
              data-aos-delay={i * 120}
            >
              <div className="relative h-40 rounded-xl [transform-style:preserve-3d] transition-transform duration-500 group-hover:[transform:rotateY(180deg)]">
                {/* front */}
                <div className="absolute inset-0 rounded-xl bg-white border border-slate-200 shadow p-4 flex flex-col justify-center items-start [backface-visibility:hidden] animate-float">
                  <div className="flex items-center gap-2 text-sm text-rose-600 font-semibold">
                    <Code className="h-4 w-4" /> {lab.title}
                  </div>
                  <div className="mt-2 text-slate-600 text-sm">{lab.desc}</div>
                </div>
                {/* back */}
                <div className="absolute inset-0 rounded-xl bg-rose-50 border border-rose-200 p-4 [transform:rotateY(180deg)] [backface-visibility:hidden]">
                  <div className="flex items-center gap-2 font-semibold text-slate-800">
                    <Layers className="h-4 w-4" /> What you'll do
                  </div>
                  <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                    {lab.tasks.map((task, idx) => (
                      <li key={idx}>{task}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Skills & Portfolio */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Skills */}
          <div data-aos="fade-right">
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Layers className="h-5 w-5 text-sky-500" /> Skills You'll Build
            </h3>
            <ul className="space-y-2 text-sm text-slate-700">
              {SKILLS.map((s, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-sky-500" /> {s}
                </li>
              ))}
            </ul>
          </div>

          {/* Portfolio Prompts */}
          <div
            data-aos="fade-up"
            className="p-4 border rounded-lg bg-white shadow-sm animate-bounce-slow"
          >
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
              <ImageIcon className="h-5 w-5" /> Portfolio Prompts
            </h3>
            <ol className="list-decimal pl-5 text-sm text-slate-700 space-y-2">
              <li>Animated banner (looping 15s)</li>
              <li>Interactive microinteraction (button UI)</li>
              <li>Mini-game prototype (player input & collision)</li>
            </ol>
          </div>

          {/* Tools & Exports */}
          <div
            data-aos="fade-left"
            className="p-4 border rounded-lg bg-white shadow-sm animate-bounce-slow"
          >
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Feather className="h-5 w-5" /> Tools & Exports
            </h3>
            <div className="text-sm text-slate-700 space-y-2">
              <div>
                <strong>Authoring:</strong> Adobe Animate (Flash heritage)
              </div>
              <div>
                <strong>Export:</strong> HTML5 Canvas, spritesheets, video
              </div>
              <div>
                <strong>Extras:</strong> Audacity for audio, TexturePacker for
                sprites
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <h2
          className="text-xl font-bold mb-4 flex items-center gap-2"
          data-aos="fade-up-left"
        >
          <ClipboardList className="h-5 w-5" /> Learning Roadmap
        </h2>
        <div
          className="flex items-center gap-4 overflow-x-auto pb-3 no-scrollbar animate-float"
          data-aos="fade-up"
        >
          {[
            { title: "Week 1-2", desc: "Foundations & Timeline" },
            { title: "Week 3-4", desc: "AS3 Concepts & Buttons" },
            { title: "Week 5-6", desc: "Complex Animation & Sound" },
            { title: "Week 7-8", desc: "Games, Export & Portfolio" },
          ].map((m, i) => (
            <div
              key={i}
              className="min-w-[220px] rounded-lg border p-4 bg-white shadow-sm"
              data-aos="flip-up"
              data-aos-delay={i * 100}
            >
              <div className="flex items-center gap-2 font-semibold text-slate-800">
                <ClipboardList className="h-4 w-4 text-rose-500" /> {m.title}
              </div>
              <div className="text-sm text-slate-600 mt-2">{m.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Careers */}
      <section className="max-w-6xl mx-auto px-6 py-8 ">
        <h2
          className="text-xl font-bold mb-4 flex items-center gap-2"
          data-aos="zoom-in"
        >
          <Sparkles className="h-5 w-5" /> Career Outcomes
        </h2>
        <div className="grid md:grid-cols-3 gap-4 animate-pulse">
          {CAREERS.map((c, i) => (
            <div
              key={i}
              className="p-4 rounded-lg border bg-white shadow-sm"
              data-aos="fade-up"
              data-aos-delay={i * 90}
            >
              <div className="font-semibold text-slate-800 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-400" /> {c}
              </div>
              <div className="text-sm text-slate-600 mt-2">
                Showcase:{" "}
                {c === "Motion Graphics Artist" ? "reel" : "portfolio piece"}
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* FAQ */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-4" data-aos="fade-up">
          Frequently Asked
        </h2>
        <div className="space-y-3">
          {[
            {
              q: "Is Flash still relevant?",
              a: "While SWF is deprecated, Flash's animation and interactivity concepts map very well to modern tools (Animate CC, Canvas) and game prototyping.",
            },
            {
              q: "Do I need programming skills?",
              a: "Basic scripting logic helps. We teach ActionScript-style concepts and how they translate to JavaScript/Canvas.",
            },
            {
              q: "Will I get a portfolio?",
              a: "Yes — labs are designed to produce portfolio-ready animated banners, interactions, and a mini prototype.",
            },
          ].map((f, i) => (
            <details
              key={i}
              open={i === 0}
              className="group rounded-lg border px-4 py-3 bg-white"
              data-aos="fade-right"
              onToggle={() => setOpenFAQ(i)}
            >
              <summary className="flex items-center justify-between cursor-pointer font-medium">
                <span>{f.q}</span>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </summary>
              <div className="mt-2 text-sm text-slate-700">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <footer className="max-w-6xl mx-auto px-6 py-12">
        <div
          className="rounded-2xl p-6 bg-gradient-to-r from-rose-600 to-amber-400 text-white shadow-lg"
          data-aos="zoom-in-up"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold">Ready to Animate & Build?</h3>
              <p className="mt-1 text-sm">
                Join the Adobe Flash Studio course and produce animated projects
                that shine — old-school craft, modern exports.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/contact"
                className="px-5 py-3 rounded-lg border border-white/30 text-white"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
